import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { PRD_SECTIONS, type PrdSection, type PrdBlock } from './data';

function PrdBadge({ text, color }: { text: string; color: string }) {
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold whitespace-nowrap"
      style={{ background: `${color}15`, color, border: `1px solid ${color}30` }}
    >
      {text}
    </span>
  );
}

function BlockRenderer({ block, sectionColor }: { block: PrdBlock; sectionColor: string }) {
  switch (block.type) {
    case 'heading':
      return (
        <h4 className="text-sm font-bold text-gray-900 mt-5 mb-2 flex items-center gap-2">
          <span className="w-1 h-4 rounded-full shrink-0" style={{ background: sectionColor }}></span>
          {block.title}
        </h4>
      );
    case 'text':
      return <p className="text-sm text-gray-600 leading-relaxed">{block.text}</p>;
    case 'list':
      return (
        <ul className="space-y-1.5">
          {block.items?.map((item, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-gray-600 leading-relaxed">
              <span className="w-1 h-1 rounded-full bg-gray-400 shrink-0 mt-2"></span>
              <span>{item}</span>
            </li>
          ))}
        </ul>
      );
    case 'rule':
      return (
        <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 space-y-2.5">
          {block.items?.map((item, i) => (
            <div key={i} className="flex items-start gap-2.5">
              <div className="w-4 h-4 flex items-center justify-center rounded-full shrink-0 mt-0.5" style={{ background: 'rgba(239,68,68,0.08)' }}>
                <i className="ri-error-warning-line text-xs text-red-500"></i>
              </div>
              <p className="text-sm text-gray-700 leading-relaxed">{item}</p>
            </div>
          ))}
        </div>
      );
    case 'table':
      return (
        <div className="rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full">
            <tbody className="divide-y divide-gray-100">
              {block.rows?.map((row, i) => (
                <tr key={i} className="hover:bg-gray-50/50 transition-colors">
                  <td className="px-4 py-2.5 text-xs font-semibold text-gray-700 whitespace-nowrap w-32 shrink-0" style={{ background: 'rgba(0,0,0,0.02)' }}>
                    {row.label}
                  </td>
                  <td className="px-4 py-2.5 text-sm text-gray-600 leading-relaxed">{row.desc}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case 'interaction':
      return (
        <div className="rounded-xl border border-gray-200 bg-white p-4 space-y-2.5">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">交互流程</p>
          {block.steps?.map((step, i) => (
            <div key={i} className="flex items-start gap-3">
              <div
                className="w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5 text-white text-xs font-bold"
                style={{ background: sectionColor }}
              >
                {i + 1}
              </div>
              <p className="text-sm text-gray-700 leading-relaxed">{step}</p>
            </div>
          ))}
        </div>
      );
    case 'note':
      return (
        <div className="flex items-start gap-2.5 px-4 py-3 rounded-lg bg-amber-50 border border-amber-100">
          <i className="ri-lightbulb-line text-sm text-amber-500 mt-0.5 shrink-0"></i>
          <p className="text-xs text-amber-700 leading-relaxed">{block.note}</p>
        </div>
      );
    default:
      return null;
  }
}

export default function PrdPage() {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('overview');
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<{ sectionId: string; blockIndex: number }[]>([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: '-15% 0px -55% 0px', threshold: 0 }
    );
    Object.values(sectionRefs.current).forEach((el) => { if (el) observer.observe(el); });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowSearchResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const setRef = (id: string) => (el: HTMLDivElement | null) => {
    sectionRefs.current[id] = el;
  };

  const scrollToSection = (id: string) => {
    setActiveSection(id);
    sectionRefs.current[id]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    if (!query.trim()) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }
    const q = query.toLowerCase();
    const results: { sectionId: string; blockIndex: number }[] = [];
    PRD_SECTIONS.forEach((sec) => {
      sec.content.forEach((block, idx) => {
        const text =
          (block.title ?? '') +
          (block.text ?? '') +
          (block.items?.join('') ?? '') +
          (block.steps?.join('') ?? '') +
          (block.note ?? '') +
          (block.rows?.map((r) => r.label + r.desc).join('') ?? '');
        if (text.toLowerCase().includes(q)) {
          results.push({ sectionId: sec.id, blockIndex: idx });
        }
      });
    });
    setSearchResults(results);
    setShowSearchResults(true);
  };

  const handleSearchResultClick = (sectionId: string, blockIndex: number) => {
    scrollToSection(sectionId);
    setShowSearchResults(false);
    setSearchQuery('');
    // Highlight the block briefly
    setTimeout(() => {
      const sectionEl = sectionRefs.current[sectionId];
      if (sectionEl) {
        const blocks = sectionEl.querySelectorAll('[data-block-index]');
        const target = blocks[blockIndex] as HTMLElement | undefined;
        if (target) {
          target.scrollIntoView({ behavior: 'smooth', block: 'center' });
          target.style.background = 'rgba(0,105,189,0.06)';
          target.style.transition = 'background 0.5s ease';
          setTimeout(() => { target.style.background = 'transparent'; }, 2000);
        }
      }
    }, 400);
  };

  const filteredSections = PRD_SECTIONS;

  return (
    <AdminLayout
      title="PRD 需求文档"
      subtitle="Sinovision 代理商资料分发平台 — 产品需求规格说明书"
    >
      <div className="flex gap-8 items-start">
        {/* Sidebar TOC */}
        <aside className="hidden xl:block w-60 shrink-0 sticky top-24">
          <div className="bg-white rounded-xl border border-gray-100 p-4 mb-3">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-3">文档目录</p>
            <nav className="space-y-0.5">
              {filteredSections.map((sec) => (
                <button
                  key={sec.id}
                  onClick={() => scrollToSection(sec.id)}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-all cursor-pointer text-left whitespace-nowrap ${
                    activeSection === sec.id
                      ? 'font-semibold text-white'
                      : 'text-gray-500 hover:text-gray-800 hover:bg-gray-50'
                  }`}
                  style={activeSection === sec.id ? { background: sec.color } : {}}
                >
                  <div className="w-4 h-4 flex items-center justify-center shrink-0">
                    <i className={`${sec.icon} text-xs`}></i>
                  </div>
                  <span className="truncate text-xs">{sec.title}</span>
                </button>
              ))}
            </nav>
          </div>

          {/* Search */}
          <div className="bg-white rounded-xl border border-gray-100 p-4" ref={searchRef}>
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-2">快速搜索</p>
            <div className="relative">
              <div className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
                <i className="ri-search-line text-xs"></i>
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                placeholder="搜索功能、规则..."
                className="w-full pl-8 pr-3 py-2 border border-gray-200 rounded-lg text-xs text-gray-700 placeholder-gray-400 focus:outline-none focus:border-gray-400 bg-white"
              />
            </div>
            {showSearchResults && searchResults.length > 0 && (
              <div className="mt-2 space-y-1 max-h-48 overflow-y-auto">
                {searchResults.slice(0, 8).map((res, i) => {
                  const sec = PRD_SECTIONS.find((s) => s.id === res.sectionId)!;
                  const block = sec.content[res.blockIndex];
                  const preview = block.title || block.text?.slice(0, 40) || '相关内容';
                  return (
                    <button
                      key={i}
                      onClick={() => handleSearchResultClick(res.sectionId, res.blockIndex)}
                      className="w-full text-left px-2.5 py-1.5 rounded-md text-xs text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer"
                    >
                      <span className="font-semibold" style={{ color: sec.color }}>{sec.title}</span>
                      <span className="text-gray-400 mx-1">›</span>
                      <span className="truncate">{preview}</span>
                    </button>
                  );
                })}
              </div>
            )}
            {showSearchResults && searchQuery && searchResults.length === 0 && (
              <p className="mt-2 text-xs text-gray-400 text-center">未找到匹配内容</p>
            )}
          </div>

          <div className="mt-3 bg-white rounded-xl border border-gray-100 p-4">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-2">文档信息</p>
            <div className="space-y-1.5 text-xs text-gray-500">
              <div className="flex items-center justify-between">
                <span>版本</span>
                <span className="font-semibold text-gray-700">v1.0</span>
              </div>
              <div className="flex items-center justify-between">
                <span>更新日期</span>
                <span className="font-semibold text-gray-700">2026.05.06</span>
              </div>
              <div className="flex items-center justify-between">
                <span>模块数</span>
                <span className="font-semibold text-gray-700">{PRD_SECTIONS.length} 个</span>
              </div>
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <div className="flex-1 min-w-0 space-y-8">
          {/* Hero */}
          <div className="rounded-2xl relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)' }}>
            <div className="px-8 py-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
                  <i className="ri-file-list-3-line text-white text-lg"></i>
                </div>
                <div>
                  <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Product Requirements Document</p>
                  <h1 className="text-white text-xl font-bold">PRD 需求文档</h1>
                </div>
              </div>
              <p className="text-white/70 text-sm leading-relaxed max-w-lg">
                本文档基于 Sinovision 代理商资料分发平台的最新系统界面编写，详细描述各页面的功能结构、交互规则和业务逻辑，用于指导后续功能开发和迭代。
              </p>
              <div className="flex flex-wrap gap-2 mt-6">
                {PRD_SECTIONS.map((sec) => (
                  <button
                    key={sec.id}
                    onClick={() => scrollToSection(sec.id)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-white/10 text-white/80 hover:bg-white/20 transition-all cursor-pointer whitespace-nowrap border border-white/10"
                  >
                    <i className={`${sec.icon} text-xs`}></i>
                    {sec.title}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Sections */}
          {PRD_SECTIONS.map((sec) => (
            <div
              key={sec.id}
              id={sec.id}
              ref={setRef(sec.id)}
              className="scroll-mt-24"
            >
              {/* Section Header */}
              <div className="flex items-center gap-4 mb-5">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                  style={{ background: `${sec.color}12` }}
                >
                  <i className={`${sec.icon} text-lg`} style={{ color: sec.color }}></i>
                </div>
                <div>
                  <h2 className="text-base font-bold text-gray-900">{sec.title}</h2>
                </div>
                <div className="flex-1 h-px bg-gray-100 ml-2"></div>
                <PrdBadge text={sec.id.toUpperCase()} color={sec.color} />
              </div>

              {/* Section Content */}
              <div className="space-y-3">
                {sec.content.map((block, idx) => (
                  <div key={idx} data-block-index={idx} className="rounded-xl p-1">
                    <BlockRenderer block={block} sectionColor={sec.color} />
                  </div>
                ))}
              </div>
            </div>
          ))}

          {/* Footer */}
          <div className="rounded-xl border border-gray-100 bg-white px-6 py-5 flex items-center justify-between flex-wrap gap-4">
            <div>
              <p className="text-sm font-bold text-gray-800">文档结束</p>
              <p className="text-xs text-gray-400 mt-0.5">如有疑问请联系产品团队</p>
            </div>
            <button
              onClick={() => navigate('/admin')}
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold text-white cursor-pointer whitespace-nowrap"
              style={{ background: '#0069BD' }}
            >
              <i className="ri-arrow-left-line text-sm"></i>
              返回后台
            </button>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
